import {
  BriefcaseBusiness,
  Contact2,
  FileText,
  Home,
  Loader2,
  Mail,
  MapPin,
  Percent,
  Warehouse,
} from "lucide-react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { Separator } from "../../../../../components/ui/separator";
import { Switch } from "../../../../../components/ui/switch";
import { formatCep, formatCnpj } from "../../../../../utils/format-utils";
import {
  ImileFranquiaRegistrationFormData,
  previewOpsDerivadas,
} from "../utils/validation-schemas";

interface RegistrationFormProps {
  form: UseFormReturn<ImileFranquiaRegistrationFormData>;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function RegistrationForm({
  form,
  onSubmit,
  isSubmitting,
}: RegistrationFormProps) {
  const watchApelido = form.watch("apelido");
  const watchDs = form.watch("ds");
  const opsPreview = previewOpsDerivadas(watchApelido, watchDs);

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="text-2xl font-bold text-foreground mb-6">
          Cadastro de Nova Franquia iMile
        </h2>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="cnpj"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <div className="relative">
                    <BriefcaseBusiness className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="cnpj"
                      placeholder="XX.XXX.XXX/XXXX-XX"
                      {...field}
                      onChange={(e) =>
                        field.onChange(formatCnpj(e.target.value))
                      }
                      className="pl-9"
                      maxLength={18}
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="razaoSocial"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="razaoSocial">Razão Social *</Label>
                  <div className="relative">
                    <Home className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="razaoSocial"
                      placeholder="Nome da empresa"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="cep"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="cep">CEP *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="cep"
                      placeholder="XXXXX-XXX"
                      {...field}
                      onChange={(e) => field.onChange(formatCep(e.target.value))}
                      className="pl-9"
                      maxLength={9}
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="emailContato"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="emailContato">Email(s) de Contato *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="emailContato"
                      type="text"
                      placeholder="contato@empresa.com | outro@empresa.com"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Para múltiplos emails, separe com | (pipe).
                  </p>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="apelido"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="apelido">Apelido / prefixo da franquia *</Label>
                  <div className="relative">
                    <Contact2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="apelido"
                      placeholder="Ex: realog"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Só o prefixo. A ops de cada base é o prefixo + a DS.
                  </p>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="ds"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="ds">DS / bases da franquia *</Label>
                  <div className="relative">
                    <Warehouse className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="ds"
                      placeholder="Ex: tji,lms"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Uma ou mais bases separadas por vírgula, como aparecem na
                    pasta do arquivo no S3.
                  </p>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="franquiaUuid"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="franquiaUuid">Franquia UUID</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="franquiaUuid"
                      placeholder="8d03027b-5236-4533-8e9e-..."
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="redutor"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="redutor">Redutor</Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="redutor"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      placeholder="1.00"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.valueAsNumber || e.target.value)
                      }
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          {/*
            Prévia das ops derivadas. É o feedback que evita o erro mais provável
            da tela: um prefixo ou uma DS errados geram uma ops que nenhum
            arquivo casa, e o romaneio entra numa ops provisória que o checkout
            não enxerga.
          */}
          {opsPreview.length > 0 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
              <p className="text-sm font-medium text-foreground">
                Ops que serão geradas para esta franquia
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {opsPreview.map((ops) => (
                  <span
                    key={ops}
                    className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 font-mono text-xs font-medium text-primary"
                  >
                    {ops}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                É com esses nomes que os romaneios da iMile são gravados e
                cobrados. Confira antes de salvar.
              </p>
            </div>
          )}

          <Controller
            name="endereco"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="space-y-2">
                <Label htmlFor="endereco">Endereço Completo *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="endereco"
                    placeholder="Rua, número, bairro, cidade, estado"
                    {...field}
                    className="pl-9"
                  />
                </div>
                {fieldState.error && (
                  <p className="text-sm font-medium text-destructive">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="nfsAtivo"
              control={form.control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 rounded-lg border bg-background">
                  <Label htmlFor="nfsAtivo" className="text-base cursor-pointer">
                    NFS (Nota Fiscal de Serviço) Ativo
                  </Label>
                  <Switch
                    id="nfsAtivo"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />

            <Controller
              name="nfsSrAtivo"
              control={form.control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 rounded-lg border bg-background">
                  <Label
                    htmlFor="nfsSrAtivo"
                    className="text-base cursor-pointer"
                  >
                    NFS Saldo Remanescente Ativo
                  </Label>
                  <Switch
                    id="nfsSrAtivo"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>

          <Separator />

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset()}
              disabled={isSubmitting}
            >
              Limpar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cadastrando...
                </>
              ) : (
                "Cadastrar Franquia"
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
